'use client'

import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  HTMLAttributes,
} from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { faClose } from '@fortawesome/pro-solid-svg-icons'
import { ImageBackgroundColors, mtc } from '../../../utils/style-helpers'
import { Button } from '../../atoms/button'
import {
  IllustrationBlock,
  type IllustrationBlockSize,
} from '../../atoms/illustration-block'
import { Text } from '../../atoms/text'
import {
  closeStyles,
  contentStyles,
  dismissStyles,
  overlayStyles,
  footerStyles,
  descriptionStyles,
  subTitleStyles,
  titleStyles,
} from './styles'

export enum DialogColors {
  LEVEL_0 = 'level0',
  LEVEL_1 = 'level1',
  PRIORITY = 'priority',
  EMPHASIS = 'emphasis',
  BRAND = 'brand',
  SUCCESS = 'success',
  WARNING = 'warning',
  DANGER = 'danger',
}

type DialogProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & {
  onClose?: () => void
}

const DialogRoot = ({ onClose, onOpenChange, ...rest }: DialogProps) => {
  const handleOnClose = (open: boolean) => {
    onOpenChange?.(open)

    if (!open) {
      onClose?.()
    }
  }

  return <DialogPrimitive.Root {...rest} onOpenChange={handleOnClose} />
}

export const DialogTrigger = DialogPrimitive.Trigger
export const DialogPortal = DialogPrimitive.Portal
export const DialogClose = DialogPrimitive.Close

export const DialogHeader = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="dialog-header"
      className={mtc('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  )
}

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={mtc(overlayStyles(), className)}
    {...props}
  />
))

export const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={mtc(footerStyles(), className)} {...props} />
)

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
    center?: boolean
    variant?: 'h1' | 'displayMd'
  }
>(({ center, variant, className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={mtc(titleStyles({ center, variant }), className)}
    {...props}
  />
))

export const DialogSubTitle = ({
  center,
  className,
  ...props
}: Omit<ComponentPropsWithoutRef<'p'>, 'color'> & { center?: boolean }) => (
  <Text className={mtc(subTitleStyles({ center }), className)} {...props} />
)

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
    center?: boolean
  }
>(({ center, className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={mtc(descriptionStyles({ center }), className)}
    {...props}
  />
))

export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    dismissClassName?: string
    onDismiss?: () => void
    size?: 'sm' | 'md' | 'lg' | 'xl'
    heading?: string
    subTitle?: string
    description?: string
    illustrationSrc?: string
    illustrationAlt?: string
    illustrationColor?: ImageBackgroundColors
    illustrationSize?: IllustrationBlockSize
  }
>(
  (
    {
      className,
      children,
      dismissClassName,
      onDismiss,
      size,
      heading,
      subTitle,
      description,
      illustrationSrc,
      illustrationAlt,
      illustrationColor,
      illustrationSize = 'md',
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay>
        <DialogPrimitive.Content
          ref={ref}
          className={mtc(contentStyles({ size }), className)}
          aria-describedby={description ? 'dialog-description' : undefined}
          {...props}
        >
          {(heading || subTitle) && (
            <div className="flex flex-col">
              {heading && <DialogTitle>{heading}</DialogTitle>}
              {subTitle && <DialogSubTitle>{subTitle}</DialogSubTitle>}
            </div>
          )}
          {illustrationSrc && (
            <IllustrationBlock
              src={illustrationSrc}
              alt={illustrationAlt ?? ''}
              size={illustrationSize}
              color={illustrationColor}
            />
          )}
          {description && <DialogDescription>{description}</DialogDescription>}

          <div className="flex flex-col gap-4 flex-1">{children}</div>

          {onDismiss && (
            <DialogPrimitive.Close className={mtc(closeStyles())} asChild>
              <Button
                aria-label="Dismiss banner"
                className={mtc(dismissStyles(), dismissClassName)}
                iconRight={faClose}
                onClick={onDismiss}
                variant="discreet"
                size="square"
              />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  )
)

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName
DialogContent.displayName = DialogPrimitive.Content.displayName
DialogFooter.displayName = 'DialogFooter'
DialogTitle.displayName = DialogPrimitive.Title.displayName
DialogDescription.displayName = DialogPrimitive.Description.displayName
DialogSubTitle.displayName = 'DialogSubTitle'

export const Dialog = Object.assign(DialogRoot, {
  Trigger: DialogPrimitive.Trigger,
  Portal: DialogPrimitive.Portal,
  Close: DialogPrimitive.Close,
  Header: DialogHeader,
  Overlay: DialogOverlay,
  Content: DialogContent,
  Footer: DialogFooter,
  Title: DialogTitle,
  SubTitle: DialogSubTitle,
  Description: DialogDescription,
})
